import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { HOTEL } from "@/data/hotel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "Front Office",
  "Food & Beverage",
  "Housekeeping",
  "Sales & Reservations",
  "Operations / Management",
  "Other",
];

const inputCls =
  "mt-2 w-full h-11 px-3 bg-paper-50 border border-ink-900/15 focus:border-sage focus:outline-none";

const WhyAzulo = () => (
  <ul className="mt-6 space-y-5 text-ink-700 leading-relaxed">
    {[
      ["A heritage group with a modern outlook", "Forty-plus years of hospitality, with a contemporary boutique sensibility."],
      ["Growing footprint across India", "New properties on the horizon — opportunities will multiply as we expand."],
      ["Considered work, considered teams", "Small teams, real ownership, real impact on the guest experience."],
      ["Cross-property mobility", "Move between our hotels and departments as you grow."],
    ].map(([t, d]) => (
      <li key={t} className="border-l-2 border-sage/50 pl-5">
        <div className="font-serif text-xl text-ink-900">{t}</div>
        <div className="mt-2 text-ink-600">{d}</div>
      </li>
    ))}
  </ul>
);

const CareersForm = ({ form, update, onSubmit, submitting }) => (
  <form
    data-testid="careers-form"
    onSubmit={onSubmit}
    className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5"
  >
    <div className="md:col-span-1">
      <label className="overline">Full Name *</label>
      <input type="text" required value={form.full_name} onChange={update("full_name")} data-testid="careers-name" className={inputCls} />
    </div>
    <div className="md:col-span-1">
      <label className="overline">Email *</label>
      <input type="email" required value={form.email} onChange={update("email")} data-testid="careers-email" className={inputCls} />
    </div>
    <div className="md:col-span-1">
      <label className="overline">Phone</label>
      <input type="tel" value={form.phone} onChange={update("phone")} data-testid="careers-phone" className={inputCls} />
    </div>
    <div className="md:col-span-1">
      <label className="overline">Role of Interest</label>
      <select value={form.role_of_interest} onChange={update("role_of_interest")} data-testid="careers-role" className={inputCls}>
        <option value="">Select a role</option>
        {ROLES.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
    </div>
    <div className="md:col-span-2">
      <label className="overline">Tell us about yourself *</label>
      <textarea
        required
        rows={5}
        value={form.message}
        onChange={update("message")}
        data-testid="careers-message"
        placeholder="Years of experience, current/last role, why you'd like to join Azulo, link to your CV (Google Drive / Dropbox)…"
        className="mt-2 w-full p-3 bg-paper-50 border border-ink-900/15 focus:border-sage focus:outline-none resize-none"
      />
    </div>
    <div className="md:col-span-2 mt-2">
      <button
        type="submit"
        disabled={submitting}
        data-testid="careers-submit"
        className="inline-flex items-center h-12 px-8 bg-sage text-paper-50 text-[11px] tracking-[0.22em] uppercase hover:bg-sage-dark transition-colors disabled:opacity-60"
      >
        {submitting ? "Submitting…" : "Submit Application"}
      </button>
    </div>
  </form>
);

const CareersHeader = () => (
  <Reveal>
    <div className="overline">Careers at Azulo</div>
    <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.98] text-ink-900 max-w-3xl">
      Build a career in
      <span className="italic text-sage-dark"> hospitality.</span>
    </h1>
    <p className="mt-6 max-w-2xl text-ink-700 text-lg leading-relaxed">
      Marwon Group has been welcoming travellers to India since 1980. As we grow our
      boutique hotel portfolio across the country, we&rsquo;re looking for
      warm, considered people who care about the details — in front office, F&amp;B,
      housekeeping, sales and reservations.
    </p>
  </Reveal>
);

const DirectApplications = () => (
  <Reveal delay={0.1}>
    <div className="mt-12 p-6 bg-paper-100 border hairline">
      <div className="overline">Direct applications</div>
      <p className="mt-3 text-ink-700 text-sm leading-relaxed">
        You may also write to us at{" "}
        <a href={`mailto:${HOTEL.careersEmail}`} className="underline hover:text-sage-dark">
          {HOTEL.careersEmail}
        </a>{" "}
        with your CV and a short note about the role you&rsquo;re interested in.
      </p>
    </div>
  </Reveal>
);

const SuccessPanel = ({ onReset }) => (
  <div data-testid="careers-success" className="mt-10 p-8 bg-paper-100 border border-sage/30">
    <div className="overline">Application Received</div>
    <p className="mt-4 text-ink-800 leading-relaxed">
      Thank you for applying to Marwon Group. We&rsquo;ve recorded your details
      and our HR team will reach out shortly to discuss next steps.
    </p>
    <button
      type="button"
      data-testid="careers-apply-again"
      onClick={onReset}
      className="mt-6 text-[11px] tracking-[0.22em] uppercase text-sage-dark hover:text-ink-900"
    >
      Submit another application →
    </button>
  </div>
);

export default function Careers() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    role_of_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/careers`, form);
      setSubmitted(true);
      toast.success("Application received. Our team will be in touch.");
      setForm({ full_name: "", email: "", phone: "", role_of_interest: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 md:pt-40 pb-24" data-testid="careers-page">
      <Seo
        title="Careers — Build a career in hospitality"
        description="Join Marwon Group — boutique hotels across India since 1980. We're hiring across operations, F&B, housekeeping, sales, and reservations. Apply today."
        path="/careers"
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <CareersHeader />

        <div className="mt-20 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl text-ink-900 leading-tight">
                Why Azulo
              </h2>
              <WhyAzulo />
            </Reveal>
            <DirectApplications />
          </div>

          <div className="md:col-span-7">
            <Reveal>
              <div className="bg-paper-50 border hairline p-8 md:p-10">
                <h2 className="font-serif text-3xl md:text-4xl text-ink-900">
                  Apply <span className="italic text-sage-dark">to join Azulo</span>
                </h2>
                <p className="mt-3 text-ink-600">
                  Fill in the form and our HR team will get back to you.
                </p>

                {submitted ? (
                  <SuccessPanel onReset={() => setSubmitted(false)} />
                ) : (
                  <CareersForm form={form} update={update} onSubmit={onSubmit} submitting={submitting} />
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
